/**
 * Email Integration Types
 * Corresponds to backend Pydantic schemas in api/routers/email.py
 */

// ============================================================================
// Enums
// ============================================================================

export enum EmailProvider {
  GMAIL = 'gmail',
  OUTLOOK = 'outlook',
  IMAP_SMTP = 'imap_smtp',
}

export enum EmailAccountStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ERROR = 'error',
  AUTHENTICATION_REQUIRED = 'authentication_required',
  SYNCING = 'syncing',
}

export enum EmailMessageStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  PROCESSED = 'processed',
  FAILED = 'failed',
  IGNORED = 'ignored',
  IRRELEVANT = 'irrelevant',
  LIMBO = 'limbo',
  USER_APPROVED = 'user_approved',
  USER_REJECTED = 'user_rejected',
}

// ============================================================================
// Email Account Types
// ============================================================================

export interface EmailAccountResponse {
  account_id: string;
  company_id: string;
  email_address: string;
  display_name: string | null;
  provider: EmailProvider;
  status: EmailAccountStatus;
  last_sync_at: string | null;
  last_error: string | null;
  auto_process_attachments: boolean;
  allowed_sender_domains: string[] | null;
  folders_to_watch: string[];
  sync_from_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailAccountCreate {
  email_address: string;
  display_name?: string;
  provider?: EmailProvider;
  auto_process_attachments?: boolean;
  allowed_sender_domains?: string[];
  folders_to_watch?: string[];
}

export interface EmailAccountCreateIMAP {
  email_address: string;
  display_name?: string;
  imap_host: string;
  imap_port?: number;
  imap_username?: string;
  imap_password: string;
  imap_use_ssl?: boolean;
  smtp_host?: string;
  smtp_port?: number;
  smtp_username?: string;
  smtp_use_ssl?: boolean;
  auto_process_attachments?: boolean;
  allowed_sender_domains?: string[];
  folders_to_watch?: string[];
}

export interface EmailAccountUpdate {
  display_name?: string;
  auto_process_attachments?: boolean;
  allowed_sender_domains?: string[];
  folders_to_watch?: string[];
  sync_from_date?: string | null;
  status?: EmailAccountStatus;
}

// Email folder/label information
export interface EmailFolderInfo {
  name: string;           // Internal name (e.g., "INBOX", "[Gmail]/Spam")
  display_name: string;   // User-friendly name (e.g., "Inbox", "Spam")
  unread_count: number;   // Number of unread messages
  total_count: number;    // Total number of messages
  is_system: boolean;     // True for system folders (INBOX, SENT, etc.)
}

export interface EmailAccountUpdateIMAP extends EmailAccountUpdate {
  imap_host?: string;
  imap_port?: number;
  imap_username?: string;
  imap_password?: string;
  imap_use_ssl?: boolean;
  smtp_host?: string;
  smtp_port?: number;
  smtp_username?: string;
  smtp_use_ssl?: boolean;
}

// ============================================================================
// OAuth Types
// ============================================================================

export interface OAuthAuthorizationResponse {
  authorization_url: string;
  state: string;
  account_id: string;
  redirect_uri: string;
}

export interface OAuthCallbackData {
  authorization_code: string;
  redirect_uri: string;
}

export interface OAuthCallbackResult {
  status: 'success' | 'error';
  message: string;
  account_id?: string;
}

// ============================================================================
// IMAP Provider Presets
// ============================================================================

export interface ProviderPreset {
  name: string;
  display_name: string;
  imap_host: string;
  imap_port: number;
  imap_use_ssl: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_use_ssl: boolean;
  notes: string;
  requires_app_password: boolean;
  help_url: string | null;
}

// ============================================================================
// Connection Test
// ============================================================================

export interface ConnectionTestResponse {
  success: boolean;
  imap_connected: boolean;
  smtp_connected: boolean;
  error_message: string | null;
  response_time_ms: number;
}

// ============================================================================
// Email Processing
// ============================================================================

export interface ProcessEmailsResponse {
  account_id: string;
  fetched: number;
  processed: number;
  failed: number;
  documents_created: number;
}

// ============================================================================
// Email Messages (for history view)
// ============================================================================

export interface EmailMessageResponse {
  message_id: string;
  account_id: string;
  company_id: string;
  subject: string | null;
  from_address: string | null;
  from_name: string | null;
  received_at: string | null;
  status: EmailMessageStatus;
  has_attachments: boolean;
  attachment_count: number;
  created_at: string;
}

export interface EmailAttachmentResponse {
  attachment_id: string;
  message_id: string;
  company_id: string;
  filename: string;
  content_type: string | null;
  size_bytes: number | null;
  /** GCS path (backend may expose as gcs_path or storage_path) */
  storage_path?: string | null;
  gcs_path?: string | null;
  document_id: string | null;
  is_document: boolean;
  downloaded: boolean;
  /** When true, user marked as not relevant; excluded from processing */
  user_excluded?: boolean;
  created_at: string;
}

export interface EmailMessageDetailResponse extends EmailMessageResponse {
  to_addresses: string[] | null;
  cc_addresses: string[] | null;
  body_text: string | null;
  body_html: string | null;
  attachments: EmailAttachmentResponse[];
}

// ============================================================================
// Filters
// ============================================================================

export interface EmailAccountFilters {
  skip?: number;
  limit?: number;
  status?: EmailAccountStatus;
}

export interface EmailMessageFilters {
  skip?: number;
  limit?: number;
  status?: EmailMessageStatus;
  account_id?: string;
}

// ============================================================================
// Wizard State
// ============================================================================

export type WizardStep =
  | 'email-entry'
  | 'gmail-oauth'
  | 'outlook-oauth'
  | 'imap-config'
  | 'success';

export interface WizardState {
  currentStep: WizardStep;
  email: string;
  provider: EmailProvider | null;
  displayName: string;
  allowedDomains: string[];
  accountId: string | null;
  // IMAP-specific
  imapPreset: string | null;
  imapHost: string;
  imapPort: number;
  imapUsername: string;
  imapPassword: string;
  imapUseSsl: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpUseSsl: boolean;
}

// ============================================================================
// Email-Container Association Types
// ============================================================================

export interface AssociateContainerRequest {
  container_id: string;
}

export interface AssociateContainerResponse {
  status: string;
  message_id: string;
  container_id: string;
  message: string;
}

// ============================================================================
// Email Counts Types
// ============================================================================

export interface EmailAccountCounts {
  account_id: string;
  email_address: string;
  inbox_unread: number;
  limbo: number;
}

export interface EmailCountsResponse {
  inbox_unread_total: number;
  limbo_total: number;
  by_account: EmailAccountCounts[];
}
